/**
 * Draft.js patching utilities
 * Patches Draft.js internal functions to prevent common errors
 */

import { setupSafeCompositionHandler } from './safeCompositionHandler';

/**
 * Patches Draft.js removeEntitiesAtEdges function to handle undefined blocks
 */
export const patchRemoveEntitiesAtEdges = () => {
    try {
        // Check if Draft.js is available
        if (typeof window !== 'undefined' && window.Draft) {
            const { removeEntitiesAtEdges } = window.Draft;
            
            if (removeEntitiesAtEdges && typeof removeEntitiesAtEdges === 'function') {
                // Store the original function
                const originalRemoveEntitiesAtEdges = removeEntitiesAtEdges;
                
                // Create a patched version
                const patchedRemoveEntitiesAtEdges = (...args) => {
                    try {
                        // Check if the first argument (entityMap) is valid
                        if (!args[0]) {
                            console.warn('removeEntitiesAtEdges: entityMap is null/undefined');
                            return;
                        }
                        
                        // Check if the second argument (block) is valid
                        if (!args[1]) {
                            console.warn('removeEntitiesAtEdges: block is null/undefined');
                            return;
                        }
                        
                        // Check if the block has the required methods
                        if (typeof args[1].getCharacterList !== 'function') {
                            console.warn('removeEntitiesAtEdges: block does not have getCharacterList method');
                            return;
                        }
                        
                        return originalRemoveEntitiesAtEdges(...args);
                    } catch (error) {
                        console.error('Error in patched removeEntitiesAtEdges:', error);
                        
                        // Log to Sentry if available
                        if (window.Sentry) {
                            window.Sentry.captureException(error, {
                                tags: {
                                    errorType: 'DRAFT_INTERNAL_PATCH_ERROR',
                                    context: 'REMOVE_ENTITIES_AT_EDGES',
                                    function: 'removeEntitiesAtEdges'
                                }
                            });
                        }
                    }
                };
                
                // Replace the original function
                window.Draft.removeEntitiesAtEdges = patchedRemoveEntitiesAtEdges;
                
                console.log('Draft.js removeEntitiesAtEdges function patched successfully');
            }
        }
    } catch (error) {
        console.error('Error patching removeEntitiesAtEdges:', error);
    }
};

/**
 * Patches Draft.js removeForBlock function to handle undefined blocks
 */
export const patchRemoveForBlock = () => {
    try {
        // Check if Draft.js is available
        if (typeof window !== 'undefined' && window.Draft) {
            // Look for removeForBlock in the Draft.js internals
            const draftInternals = window.Draft;
            
            if (draftInternals && typeof draftInternals === 'object') {
                // Try to find and patch removeForBlock
                const patchFunction = (obj, path = '') => {
                    for (const key in obj) {
                        if (obj.hasOwnProperty(key)) {
                            const value = obj[key];
                            
                            if (typeof value === 'function' && key.includes('removeForBlock')) {
                                const originalFunction = value;
                                
                                const patchedFunction = (...args) => {
                                    try {
                                        // Check if block is valid
                                        if (!args[1]) {
                                            console.warn('removeForBlock: block is null/undefined');
                                            return;
                                        }
                                        
                                        // Check if block has getCharacterList method
                                        if (typeof args[1].getCharacterList !== 'function') {
                                            console.warn('removeForBlock: block does not have getCharacterList method');
                                            return;
                                        }
                                        
                                        return originalFunction(...args);
                                    } catch (error) {
                                        console.error('Error in patched removeForBlock:', error);
                                        
                                        // Log to Sentry if available
                                        if (window.Sentry) {
                                            window.Sentry.captureException(error, {
                                                tags: {
                                                    errorType: 'DRAFT_INTERNAL_PATCH_ERROR',
                                                    context: 'REMOVE_FOR_BLOCK',
                                                    function: 'removeForBlock'
                                                }
                                            });
                                        }
                                    }
                                };
                                
                                obj[key] = patchedFunction;
                                console.log(`Patched removeForBlock function at ${path}.${key}`);
                            } else if (typeof value === 'object' && value !== null) {
                                patchFunction(value, `${path}.${key}`);
                            }
                        }
                    }
                };
                
                patchFunction(draftInternals);
            }
        }
    } catch (error) {
        console.error('Error patching removeForBlock:', error);
    }
};

/**
 * Patches Draft.js block operations to handle undefined blocks
 */
export const patchBlockOperations = () => {
    try {
        // Check if Draft.js is available
        if (typeof window !== 'undefined' && window.Draft) {
            const { ContentState, EditorState } = window.Draft;
            
            if (ContentState && typeof ContentState === 'function') {
                // Patch ContentState.getBlockForKey
                const originalGetBlockForKey = ContentState.prototype.getBlockForKey;
                
                if (originalGetBlockForKey) {
                    ContentState.prototype.getBlockForKey = function(blockKey) {
                        try {
                            if (!blockKey) {
                                console.warn('getBlockForKey: blockKey is null/undefined');
                                return null;
                            }
                            
                            const block = originalGetBlockForKey.call(this, blockKey);
                            
                            if (!block) {
                                console.warn('getBlockForKey: block not found for key:', blockKey);
                                return null;
                            }
                            
                            // Validate block structure
                            if (typeof block.getCharacterList !== 'function') {
                                console.warn('getBlockForKey: block does not have getCharacterList method');
                                return null;
                            }
                            
                            return block;
                        } catch (error) {
                            console.error('Error in patched getBlockForKey:', error);
                            return null;
                        }
                    };
                    
                    console.log('Draft.js ContentState.getBlockForKey function patched successfully');
                }
            }

            // Patch EditorState.getBlockTree to handle undefined results
            if (EditorState && typeof EditorState === 'function') {
                const originalGetBlockTree = EditorState.prototype.getBlockTree;
                
                if (originalGetBlockTree) {
                    EditorState.prototype.getBlockTree = function(blockKey) {
                        try {
                            if (!blockKey) {
                                console.warn('getBlockTree: blockKey is null/undefined');
                                return null;
                            }
                            
                            const blockTree = originalGetBlockTree.call(this, blockKey);
                            
                            if (!blockTree) {
                                console.warn('getBlockTree: blockTree not found for key:', blockKey);
                                return null;
                            }
                            
                            // Validate blockTree structure
                            if (typeof blockTree.getIn !== 'function') {
                                console.warn('getBlockTree: blockTree does not have getIn method');
                                return null;
                            }
                            
                            return blockTree;
                        } catch (error) {
                            console.error('Error in patched getBlockTree:', error);
                            return null;
                        }
                    };
                    
                    console.log('Draft.js EditorState.getBlockTree function patched successfully');
                }
            }
        }
    } catch (error) {
        console.error('Error patching block operations:', error);
    }
};

/**
 * Sets up comprehensive Draft.js patching
 */
export const setupDraftPatching = () => {
    try {
        // Wait for Draft.js to be available
        const checkDraft = () => {
            if (typeof window !== 'undefined' && window.Draft) {
                patchRemoveEntitiesAtEdges();
                patchRemoveForBlock();
                patchBlockOperations();
                setupSafeCompositionHandler();
            } else {
                // Retry after a short delay
                setTimeout(checkDraft, 100);
            }
        };
        
        checkDraft();
    } catch (error) {
        console.error('Error setting up Draft.js patching:', error);
    }
};

/**
 * Monitors for Draft.js internal errors
 */
export const setupDraftInternalErrorMonitoring = () => {
    // Monitor for Draft.js internal errors
    window.addEventListener('error', (event) => {
        if (event.error && 
            event.error.message && 
            (event.error.message.includes('getCharacterList') ||
             event.error.message.includes('removeForBlock') ||
             event.error.message.includes('removeEntitiesAtEdges'))) {
            
            console.warn('Draft.js internal error detected:', event.error.message);
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(event.error, {
                    tags: {
                        errorType: 'DRAFT_INTERNAL_ERROR',
                        context: 'GLOBAL_ERROR_HANDLER',
                        component: 'DraftEditor'
                    }
                });
            }
            
            // Prevent the error from crashing the app
            event.preventDefault();
        }
    });
};

export default {
    patchRemoveEntitiesAtEdges,
    patchRemoveForBlock,
    patchBlockOperations,
    setupDraftPatching,
    setupDraftInternalErrorMonitoring
};
